/**
 * Client-side credential management — calls Cognito WebAuthn APIs directly.
 *
 * Uses the user's access token (with aws.cognito.signin.user.admin scope)
 * to list/delete/register passkeys. No Lambda proxy needed since these are
 * user-scoped, token-authenticated operations.
 *
 * Passkey registration uses the CairnPasskeys plugin which delegates to
 * Android Credential Manager on native and navigator.credentials on web.
 */

import { Capacitor } from '@capacitor/core'
import { authService } from './auth-service.js'
import { cognitoCall } from './cognito-api.js'
import { logger } from '../../utils/logger.js'
import { CairnPasskeys } from 'cairn-passkeys'

const log = logger.create('credentials')

export interface PasskeyInfo {
  credentialId: string
  friendlyName?: string
  relyingPartyId?: string
  createdAt?: string
}

export interface LinkedProvider {
  name: string
  email?: string
}

export interface CredentialsResponse {
  passkeys: PasskeyInfo[]
  providers: LinkedProvider[]
}

export interface CredentialServiceDeps {
  getAccessToken: () => string | null
  cognitoCall: typeof cognitoCall
}

const defaultCredentialDeps: CredentialServiceDeps = {
  getAccessToken: () => authService.getAccessToken(),
  cognitoCall,
}

function requireAccessToken(deps: CredentialServiceDeps = defaultCredentialDeps): string {
  const token = deps.getAccessToken()
  if (!token) throw new Error('Not authenticated')
  return token
}

/** List registered passkeys for the current user. */
export async function listPasskeys(
  deps: CredentialServiceDeps = defaultCredentialDeps
): Promise<PasskeyInfo[]> {
  const token = requireAccessToken(deps)
  const data = await deps.cognitoCall('ListWebAuthnCredentials', {
    AccessToken: token,
  })

  const credentials = (data.Credentials ?? []) as Array<{
    CredentialId?: string
    FriendlyCredentialName?: string
    RelyingPartyId?: string
    CreatedAt?: number
  }>

  return credentials.map(c => {
    const info: PasskeyInfo = { credentialId: c.CredentialId ?? '' }
    if (c.FriendlyCredentialName) info.friendlyName = c.FriendlyCredentialName
    if (c.RelyingPartyId) info.relyingPartyId = c.RelyingPartyId
    if (c.CreatedAt) info.createdAt = new Date(c.CreatedAt * 1000).toISOString()
    return info
  })
}

/** Delete a passkey by credential ID. */
export async function deletePasskey(
  credentialId: string,
  deps: CredentialServiceDeps = defaultCredentialDeps
): Promise<void> {
  const token = requireAccessToken(deps)
  await deps.cognitoCall('DeleteWebAuthnCredential', {
    AccessToken: token,
    CredentialId: credentialId,
  })
}

/**
 * Start passkey registration.
 *
 * On native: opens /auth/register-passkey in a Chrome Custom Tab. Chrome
 * handles the full WebAuthn ceremony with biometric support. The page
 * redirects back via deep link when done.
 *
 * On web: uses the cairn-passkeys plugin (browser WebAuthn API) directly.
 */
export async function registerPasskey(
  deps: CredentialServiceDeps = defaultCredentialDeps
): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return registerPasskeyNative(deps)
  }
  return registerPasskeyWeb(deps)
}

/** Native: use Credential Manager plugin directly for passkey registration. */
async function registerPasskeyNative(deps: CredentialServiceDeps): Promise<void> {
  const token = requireAccessToken(deps)

  // 1. Get creation options from Cognito
  log.info('Starting native passkey registration...')
  const data = await deps.cognitoCall('StartWebAuthnRegistration', {
    AccessToken: token,
  })

  const options = data.CredentialCreationOptions as Record<string, unknown>
  const requestJson = JSON.stringify(options)
  log.info('Passkey creation options:', requestJson)

  // 2. Run WebAuthn ceremony via native Credential Manager plugin
  const { responseJson } = await CairnPasskeys.createCredential({ requestJson })
  log.info('Passkey creation response:', responseJson)
  const credential = JSON.parse(responseJson)

  // 3. Complete registration with Cognito
  log.info('Completing registration with Cognito...')
  await deps.cognitoCall('CompleteWebAuthnRegistration', {
    AccessToken: token,
    Credential: credential,
  })
}

/** Web: use browser WebAuthn API directly via plugin. */
async function registerPasskeyWeb(deps: CredentialServiceDeps): Promise<void> {
  const token = requireAccessToken(deps)

  // 1. Get creation options from Cognito
  const data = await deps.cognitoCall('StartWebAuthnRegistration', {
    AccessToken: token,
  })

  const options = data.CredentialCreationOptions as Record<string, unknown>
  const requestJson = JSON.stringify(options)
  log.debug('Passkey creation options:', requestJson)

  // 2. Run WebAuthn ceremony via plugin (browser navigator.credentials API)
  const { responseJson } = await CairnPasskeys.createCredential({ requestJson })
  log.debug('Passkey creation response:', responseJson)
  const credential = JSON.parse(responseJson)

  // 3. Complete registration with Cognito
  log.debug('Completing registration with Cognito...')
  await deps.cognitoCall('CompleteWebAuthnRegistration', {
    AccessToken: token,
    Credential: credential,
  })
}

/**
 * List credentials (passkeys + linked providers) for the security settings page.
 * Fetches independently so linked providers still show if passkeys fail
 * (e.g. federated-only users who aren't yet linked to a native account).
 */
export async function listCredentials(
  deps: CredentialServiceDeps = defaultCredentialDeps
): Promise<CredentialsResponse> {
  const [passkeys, providers] = await Promise.all([
    listPasskeys(deps).catch(err => {
      log.warn('Could not list passkeys:', err)
      return [] as PasskeyInfo[]
    }),
    getLinkedProviders(deps),
  ])

  return { passkeys, providers }
}

/** Fetch linked identity providers from the Cognito userInfo endpoint. */
async function getLinkedProviders(
  deps: CredentialServiceDeps = defaultCredentialDeps
): Promise<LinkedProvider[]> {
  try {
    const token = requireAccessToken(deps)
    const data = await deps.cognitoCall('GetUser', { AccessToken: token })

    const attrs = (data.UserAttributes ?? []) as Array<{ Name: string; Value: string }>
    const identitiesAttr = attrs.find(a => a.Name === 'identities')?.Value
    const email = attrs.find(a => a.Name === 'email')?.Value

    if (!identitiesAttr) return []

    const identities = JSON.parse(identitiesAttr) as Array<{ providerName: string }>
    const seen = new Set<string>()
    const providers: LinkedProvider[] = []

    for (const identity of identities) {
      if (!seen.has(identity.providerName)) {
        seen.add(identity.providerName)
        const provider: LinkedProvider = { name: identity.providerName }
        if (email) provider.email = email
        providers.push(provider)
      }
    }

    return providers
  } catch {
    return []
  }
}
